import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { addDays, format, parse } from "date-fns";
import ControlWrapper from "./o2-control-wrapper";
import O2Button from "./o2-button";
import DatePicker from "./o2-date-picker";
var g_idSeq = 1;

function O2DateField(props) {
  var _useState = useState(false),
      open = _useState[0],
      setOpen = _useState[1];

  var _useState2 = useState(props.defaultValue || ''),
      internalValue = _useState2[0],
      setInternalValue = _useState2[1];

  var _useState3 = useState(function () {
    return 'o2-react-theme-dateinput-' + g_idSeq++;
  }),
      generatedId = _useState3[0];

  var children = props.children,
      id = props.id,
      wrapperId = props.wrapperId,
      className = props.className,
      label = props.label,
      inline = props.inline,
      block = props.block,
      inlineVertical = props.inlineVertical,
      inlineLabel = props.inlineLabel,
      indentLeft = props.indentLeft,
      indentRight = props.indentRight,
      indentBottom = props.indentBottom,
      fakeLabel = props.fakeLabel,
      validationText = props.validationText,
      validationType = props.validationType,
      note = props.note,
      labelNote = props.labelNote,
      wrapperModifiers = props.wrapperModifiers,
      wrapperClassName = props.wrapperClassName,
      size = props.size,
      big = props.big,
      mobileFull = props.mobileFull,
      spinner = props.spinner,
      dateFormat = props.dateFormat,
      showTimeSelect = props.showTimeSelect,
      showTimeSelectOnly = props.showTimeSelectOnly,
      timeInterval = props.timeInterval,
      onChange = props.onChange,
      value = props.value,
      attributes = _objectWithoutPropertiesLoose(props, ["children", "id", "wrapperId", "className", "label", "inline", "block", "inlineVertical", "inlineLabel", "indentLeft", "indentRight", "indentBottom", "fakeLabel", "validationText", "validationType", "note", "labelNote", "wrapperModifiers", "wrapperClassName", "size", "big", "mobileFull", "spinner", "dateFormat", "showTimeSelect", "showTimeSelectOnly", "timeInterval", "onChange", "value"]);

  var usedId = id || generatedId;
  var dateInput = useRef(null);

  var handleClick = function handleClick() {
    if (value === undefined) {
      setInternalValue(dateInput.current.value);
    }

    setOpen(true);
  };

  var handleChange = function handleChange(date) {
    var setter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;
    setter.call(dateInput.current, date);
    var changeEvent;

    if (typeof CustomEvent === 'function') {
      changeEvent = new CustomEvent('change', {
        bubbles: true
      });
    } else {
      changeEvent = document.createEvent('CustomEvent');
      changeEvent.initEvent('change', true, true);
    }

    dateInput.current.dispatchEvent(changeEvent);
  };

  var inputClasses = classNames("o2-control__input", className, validationType ? "o2-control__validation--" + validationType : false, size ? "o2-control--" + size : false, {
    "o2-control__input--big": big,
    "o2-control--mobile-full": mobileFull
  });

  var incrementDate = function incrementDate() {
    var currentValue = value || dateInput.current.value;
    var localDate;

    if (currentValue && currentValue !== "") {
      localDate = parse(currentValue, dateFormat, new Date());
    } else {
      localDate = new Date();
    }

    handleChange(format(addDays(localDate, 1), dateFormat));
  };

  var decrementDate = function decrementDate() {
    var currentValue = value || dateInput.current.value;
    var localDate;

    if (currentValue && currentValue !== "") {
      localDate = parse(currentValue, dateFormat, new Date());
    } else {
      localDate = new Date();
    }

    handleChange(format(addDays(localDate, -1), dateFormat));
  };

  var toggle = function toggle() {
    setOpen(false);
  };

  var handleInputChange = function handleInputChange(e) {
    if (value === undefined && open) {
      setInternalValue(dateInput.current.value);
    }

    if (onChange) {
      onChange(e);
    }
  };

  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ControlWrapper, {
    id: wrapperId,
    controlId: usedId,
    note: note,
    label: label,
    labelNote: labelNote,
    inline: inline,
    block: block,
    inlineVertical: inlineVertical,
    inlineLabel: inlineLabel,
    indentLeft: indentLeft,
    indentRight: indentRight,
    indentBottom: indentBottom,
    fakeLabel: fakeLabel,
    wrapperModifiers: wrapperModifiers,
    validationType: validationType,
    validationText: validationText,
    wrapperClassName: wrapperClassName,
    btnRight: true
  }, /*#__PURE__*/React.createElement("div", {
    className: "o2-control__inner-container"
  }, /*#__PURE__*/React.createElement("input", _extends({
    id: usedId,
    value: value,
    onChange: handleInputChange,
    className: inputClasses,
    ref: dateInput
  }, attributes))), spinner && /*#__PURE__*/React.createElement("div", {
    className: "o2-control__spinner"
  }, /*#__PURE__*/React.createElement("button", {
    className: "o2-btn o2-btn--spinner",
    onClick: incrementDate
  }, /*#__PURE__*/React.createElement("span", {
    className: "o2-icon o2-icon--rounded-triangle-up-fill"
  })), /*#__PURE__*/React.createElement("button", {
    className: "o2-btn o2-btn--spinner",
    onClick: decrementDate
  }, /*#__PURE__*/React.createElement("span", {
    className: "o2-icon o2-icon--rounded-triangle-down-fill"
  }))), /*#__PURE__*/React.createElement(O2Button, {
    icon: "calendar-fill",
    form: "addon",
    onClick: handleClick
  })), /*#__PURE__*/React.createElement(DatePicker, {
    isOpen: open,
    target: usedId,
    toggle: toggle,
    selected: value || internalValue || '',
    onChange: handleChange,
    showTimeSelect: showTimeSelect,
    showTimeSelectOnly: showTimeSelectOnly,
    dateFormat: dateFormat,
    timeInterval: timeInterval
  }));
}

O2DateField.propTypes = {
  id: PropTypes.string,
  wrapperId: PropTypes.string,
  children: PropTypes.node,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  className: PropTypes.string,
  inline: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf(["md"])]),
  block: PropTypes.bool,
  inlineVertical: PropTypes.oneOf(["top", "middle", "bottom"]),
  inlineLabel: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf(["lg"])]),
  indentLeft: PropTypes.bool,
  indentRight: PropTypes.bool,
  indentBottom: PropTypes.bool,
  fakeLabel: PropTypes.bool,
  validationText: PropTypes.string,
  validationType: PropTypes.oneOf(["error", "warning", "success", false]),
  note: PropTypes.string,
  labelNote: PropTypes.string,
  wrapperModifiers: PropTypes.string,
  wrapperClassName: PropTypes.string,
  size: PropTypes.oneOf(["xs", "date", "small", "medium", "large", "xlarge", "full-width", "auto-width"]),
  big: PropTypes.bool,
  mobileFull: PropTypes.bool,
  spinner: PropTypes.bool,
  dateFormat: PropTypes.string,
  showTimeSelect: PropTypes.bool,
  showTimeSelectOnly: PropTypes.bool,
  timeInterval: PropTypes.number,
  value: PropTypes.string
};
O2DateField.defaultProps = {
  size: "date",
  dateFormat: "dd.MM.yyyy",
  showTimeSelect: false,
  showTimeSelectOnly: false,
  timeInterval: 30
};
export default O2DateField;